import React from "react";
import { motion } from "framer-motion";
import { Award, Globe2, Ship, Network, ShieldCheck, ArrowRight } from "lucide-react";
import { Page } from "@/components/Page";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal, RevealStagger, StaggerItem } from "@/components/Reveal";
import { Button } from "@/components/Button";
import { StatCounter } from "@/components/StatCounter";
import { ASSETS, IMAGES, TIMELINE, COMPANY, CERTIFICATIONS } from "@/data/site";

const EXPERTISE = [
  { icon: Ship, title: "Maritime Specialisation", text: "Deep expertise in vessel chartering, agency and port logistics built over two decades of operations." },
  { icon: Globe2, title: "Global Operations", text: "Coordinated movement across Asia, the Middle East, Africa and Europe through a trusted agent network." },
  { icon: Network, title: "International Agent Network", text: "A curated network of partners ensuring reliable door-to-door execution across global trade lanes." },
  { icon: ShieldCheck, title: "Institutional Trust", text: "A long-standing reputation founded on integrity, transparency and operational discipline." },
];

const About = () => {
  return (
    <Page>
      <SEO title="About Us" description="Established in 2002, Speed Shipping Agencies is an international maritime logistics company with deep expertise in freight forwarding, vessel chartering and supply chain solutions." />

      <PageHero
        eyebrow="About Speed Shipping Agencies"
        title="Two decades of maritime expertise and institutional trust"
        intro="Since 2002, Speed Shipping Agencies has delivered integrated logistics and maritime solutions across international trade corridors — built on precision, reliability and global reach."
        image={IMAGES.containers}
        watermark
      />

      {/* Narrative */}
      <section className="py-24 lg:py-32">
        <div className="ssa-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-7">
              <SectionHeading
                eyebrow="Our Story"
                title="A heritage of reliability in global shipping"
              />
              <div className="mt-7 space-y-5 text-[15px] leading-relaxed text-secondary-ssa">
                <p>
                  Founded in Kolkata in 2002, Speed Shipping Agencies began as a specialist maritime
                  logistics and freight forwarding practice. From the outset, the company was built on a
                  simple conviction — that global trade depends on partners who are precise, dependable and
                  accountable at every stage of the journey.
                </p>
                <p>
                  Over more than two decades, SSA has grown into an integrated logistics organisation
                  serving importers, exporters, shipping lines, manufacturers and project stakeholders.
                  Our capabilities span customs clearing, freight forwarding, warehousing, inland
                  transportation, project cargo, vessel chartering and marine insurance advisory.
                </p>
                <p>
                  Today, through a curated international agent network spanning Asia, the Middle East,
                  Africa and Europe, we coordinate complex movements with the discipline and transparency
                  that have defined our reputation since the very beginning.
                </p>
              </div>
            </div>
            <div className="lg:col-span-5">
              <Reveal>
                <div className="rounded-2xl overflow-hidden shadow-[0_30px_70px_rgba(8,28,58,0.2)]">
                  <img src={IMAGES.aerialPort} alt="Aerial view of a busy container port" className="w-full h-[420px] object-cover" />
                </div>
              </Reveal>
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="rounded-2xl border border-[color:var(--ssa-border-subtle)] bg-white p-6">
                  <StatCounter value={2002} dark={false} separator="" label="Year Established" />
                </div>
                <div className="rounded-2xl border border-[color:var(--ssa-border-subtle)] bg-white p-6">
                  <StatCounter value={40} suffix="+" dark={false} label="Agent Partners" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise */}
      <section className="py-24 lg:py-32 ocean-sheen">
        <div className="ssa-container">
          <SectionHeading align="center" eyebrow="Industry Expertise" title="What sets Speed Shipping Agencies apart" className="mb-14" />
          <RevealStagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {EXPERTISE.map((e) => (
              <StaggerItem key={e.title}>
                <div className="h-full rounded-2xl bg-white border border-[color:var(--ssa-border-subtle)] p-7">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy text-turquoise">
                    <e.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-6 text-lg text-navy">{e.title}</h3>
                  <p className="mt-3 text-sm text-secondary-ssa leading-relaxed">{e.text}</p>
                </div>
              </StaggerItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Certifications & Global Affiliations */}
      <section className="py-24 lg:py-32" data-testid="certifications-section">
        <div className="ssa-container">
          <SectionHeading
            align="center"
            eyebrow="Credibility & Compliance"
            title="Certifications & Global Affiliations"
            intro="Our standards are validated by internationally recognised institutions — a reflection of operational excellence, compliance and worldwide professional standing."
            className="mb-14"
          />
          <RevealStagger className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {CERTIFICATIONS.map((c) => (
              <StaggerItem key={c.abbr}>
                <div
                  data-testid={`certification-card-${c.abbr.replace(/\s/g, "-").toLowerCase()}`}
                  className="group relative h-full rounded-2xl overflow-hidden border border-[color:var(--ssa-border-subtle)] bg-white p-8 transition-shadow duration-300 hover:shadow-[0_22px_55px_rgba(8,28,58,0.14)]"
                >
                  <span className="absolute top-0 inset-x-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: "var(--ssa-gold)" }} />
                  <div className="flex items-start justify-between">
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-navy text-turquoise">
                      <c.icon className="h-7 w-7" />
                    </div>
                    <span className="eyebrow text-gold mt-2" style={{ letterSpacing: "0.22em" }}>Affiliation</span>
                  </div>
                  <div className="mt-7 font-display text-3xl text-navy tracking-tight">{c.abbr}</div>
                  <div className="mt-1 text-sm font-medium text-royal">{c.title}</div>
                  <div className="my-5 gold-divider" />
                  <p className="text-sm text-secondary-ssa leading-relaxed">{c.text}</p>
                </div>
              </StaggerItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Timeline */}
      <section className="relative py-24 lg:py-32 bg-ink overflow-hidden">
        <img
          src={ASSETS.emblem}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[680px] max-w-[90%] opacity-[0.05] select-none"
        />
        <div className="ssa-container relative">
          <SectionHeading dark align="center" eyebrow="Corporate Timeline" title="A journey of measured, deliberate growth" className="mb-16" />

          <div className="relative max-w-4xl mx-auto">
            <div className="absolute left-4 sm:left-1/2 top-0 h-full w-px bg-white/15 sm:-translate-x-1/2" />
            <div className="space-y-12">
              {TIMELINE.map((m, i) => (
                <Reveal key={m.year} delay={i * 0.05}>
                  <div className={`relative flex flex-col sm:flex-row gap-6 ${i % 2 === 1 ? "sm:flex-row-reverse" : ""}`}>
                    <div className="hidden sm:block sm:w-1/2" />
                    <div className="absolute left-4 sm:left-1/2 top-1.5 -translate-x-1/2">
                      <span className="block h-3.5 w-3.5 rounded-full bg-turquoise ring-4 ring-[color:var(--ssa-ink)]" />
                    </div>
                    <div className="pl-12 sm:pl-0 sm:w-1/2">
                      <div className={`glass-dark rounded-2xl p-6 ${i % 2 === 1 ? "sm:mr-10" : "sm:ml-10"}`}>
                        <div className="font-display text-3xl text-gold">{m.year}</div>
                        <h3 className="mt-2 text-lg text-white">{m.title}</h3>
                        <p className="mt-2 text-sm text-white/70 leading-relaxed">{m.text}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Reputation CTA */}
      <section className="py-24 lg:py-28">
        <div className="ssa-container">
          <div className="rounded-3xl navy-gradient grain relative overflow-hidden p-10 lg:p-16 text-center">
            <Award className="h-9 w-9 text-gold mx-auto" />
            <h2 className="mt-5 text-white text-3xl sm:text-4xl max-w-2xl mx-auto leading-tight">
              {COMPANY.tagline}
            </h2>
            <p className="mt-5 text-white/75 max-w-xl mx-auto">
              A long-standing reputation, trusted across international trade and shipping networks.
            </p>
            <div className="mt-8 flex justify-center">
              <Button to="/services" variant="light" size="xl">
                Discover our services <ArrowRight className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Page>
  );
};

export default About;
